/**
 * Spanish tax ID (NIF) validation utilities.
 *
 * SYNC NOTE: This validation logic is duplicated in:
 *  - api:              libs/core/src/validation/spanish-tax-id.ts
 *  - app-clubsensei-com: src/helpers/spanish-tax-id.ts (this file)
 *  - mysensei:         lib/helpers/spanish_tax_id.dart
 *  - sensei-admin:     lib/helpers/spanish_tax_id.dart
 * Changes to validation rules must be applied to all copies.
 *
 * Supports:
 *  - DNI: 8 digits + control letter (e.g., 12345678Z)
 *  - NIE: X/Y/Z + 7 digits + control letter (e.g., X1234567L)
 *  - CIF: letter + 7 digits + control digit/letter (e.g., B12345678)
 *
 * Algorithms sourced from:
 *  - Ministerio del Interior (DNI/NIE control letter)
 *  - AEAT (CIF control digit/letter)
 */

const DNI_REGEX = /^(\d{8})([A-Z])$/;
const NIE_REGEX = /^([XYZ])(\d{7})([A-Z])$/;
const CIF_REGEX = /^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/;

const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const CIF_CONTROL_LETTERS = "JABCDEFGHI";

/**
 * Validate a Spanish DNI (8 digits + letter).
 * The control letter = "TRWAGMYFPDXBNJZSQVHLCKE"[number % 23].
 */
function isValidDni(value: string): boolean {
  const match = value.match(DNI_REGEX);
  if (!match) return false;

  const number = parseInt(match[1], 10);
  const letter = match[2];
  return DNI_LETTERS[number % 23] === letter;
}

/**
 * Validate a Spanish NIE (X/Y/Z + 7 digits + letter).
 * Replace the prefix: X=0, Y=1, Z=2, then apply DNI algorithm.
 */
function isValidNie(value: string): boolean {
  const match = value.match(NIE_REGEX);
  if (!match) return false;

  const prefixMap: Record<string, string> = { X: "0", Y: "1", Z: "2" };
  const asNumber = prefixMap[match[1]] + match[2];
  const letter = match[3];
  return DNI_LETTERS[parseInt(asNumber, 10) % 23] === letter;
}

/**
 * Validate a Spanish CIF (legal entity tax ID).
 *
 * Format: letter + 7 digits + control character.
 *
 * Algorithm:
 * 1. Sum digits at even positions (0-indexed: positions 1, 3, 5)
 * 2. For digits at odd positions (0-indexed: 0, 2, 4, 6): multiply by 2,
 *    then sum the resulting digits (e.g., 16 → 1+6 = 7)
 * 3. Total = even sum + odd sum
 * 4. Control digit = (10 - (total % 10)) % 10
 *
 * Control character type depends on the entity letter:
 * - N, P, Q, R, S, W → must be a letter (JABCDEFGHI)
 * - A, B, E, H → must be a digit
 * - Others (C, D, F, G, J, K, L, M, U, V) → either digit or letter is valid
 */
function isValidCif(value: string): boolean {
  const match = value.match(CIF_REGEX);
  if (!match) return false;

  const entityLetter = match[1];
  const digits = match[2];
  const control = match[3];

  let evenSum = 0;
  let oddSum = 0;

  for (let i = 0; i < 7; i++) {
    const digit = parseInt(digits[i], 10);
    if (i % 2 === 0) {
      // Odd position (1st, 3rd, 5th, 7th) — multiply by 2, sum digits
      const doubled = digit * 2;
      oddSum += doubled < 10 ? doubled : Math.floor(doubled / 10) + (doubled % 10);
    } else {
      // Even position (2nd, 4th, 6th) — add directly
      evenSum += digit;
    }
  }

  const controlDigit = (10 - ((evenSum + oddSum) % 10)) % 10;
  const controlLetter = CIF_CONTROL_LETTERS[controlDigit];

  // Entity types that require alphabetic control
  if ("NPQRSW".includes(entityLetter)) {
    return control === controlLetter;
  }

  // Entity types that require numeric control
  if ("ABEH".includes(entityLetter)) {
    return control === String(controlDigit);
  }

  // Others accept either
  return control === String(controlDigit) || control === controlLetter;
}

/**
 * Normalize a Spanish tax ID: uppercase, remove spaces and hyphens.
 */
export function normalizeTaxId(value: string): string {
  return value.toUpperCase().replace(/[\s-]/g, "");
}

/**
 * Validate a Spanish personal ID (DNI or NIE only).
 * Use this for individual persons — CIF is not accepted.
 */
export function isValidPersonalId(value: string): boolean {
  const normalized = normalizeTaxId(value);
  return isValidDni(normalized) || isValidNie(normalized);
}

/**
 * Validate any Spanish tax ID (DNI, NIE, or CIF).
 * Use this for billing profiles where the entity can be a person or a company.
 */
export function isValidSpanishTaxId(value: string): boolean {
  const normalized = normalizeTaxId(value);
  return isValidDni(normalized) || isValidNie(normalized) || isValidCif(normalized);
}
